import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import pogImage from "../../../assets/casino/pog.png";
import cardBackImage from "../../../assets/cards/CardsBack/Deck01.png";
import {
  VideoPokerApiError,
  dealVideoPokerRound,
  drawVideoPokerRound,
  getActiveVideoPokerRound,
  getVideoPokerProfile,
} from "./videoPoker/videoPokerApi";

const LOCAL_PREVIEW_HOSTS = new Set(["localhost", "127.0.0.1"]);
const DEV_PREVIEW_BALANCE = 50000;
const DEFAULT_BET = 100;
const MIN_BET = 2;
const MAX_SAFE_BET = Math.floor(Number.MAX_SAFE_INTEGER / 10);
const CARD_FLIP_GAP_MS = 170;
const CARD_FLIP_DURATION_MS = 420;
const DISCARD_DURATION_MS = 420;
const REPLACEMENT_SETTLE_MS = 390;

const RANKS = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "j", "q", "k", "a"];
const SUITS = ["c", "d", "h", "s"];
const FULL_DECK = RANKS.flatMap((rank) => SUITS.map((suit) => `${rank}${suit}`));

const PAYTABLE = [
  { key: "ROYAL_FLUSH", label: "ROYAL FLUSH", multiplier: "×10" },
  { key: "STRAIGHT_FLUSH", label: "STRAIGHT FLUSH", multiplier: "×6" },
  { key: "FOUR_OF_A_KIND", label: "FOUR OF A KIND", multiplier: "×5" },
  { key: "FULL_HOUSE", label: "FULL HOUSE", multiplier: "×3.5" },
  { key: "FLUSH", label: "FLUSH", multiplier: "×3" },
  { key: "STRAIGHT", label: "STRAIGHT", multiplier: "×2.5" },
  { key: "THREE_OF_A_KIND", label: "THREE OF A KIND", multiplier: "×2" },
  { key: "TWO_PAIR", label: "TWO PAIR", multiplier: "×1.5" },
  { key: "ONE_PAIR", label: "ONE PAIR", multiplier: "×1" },
];

const PAYOUT_NUMERATORS = {
  ROYAL_FLUSH: 10,
  STRAIGHT_FLUSH: 6,
  FOUR_OF_A_KIND: 5,
  FULL_HOUSE: 7,
  FLUSH: 3,
  STRAIGHT: 5,
  THREE_OF_A_KIND: 2,
  TWO_PAIR: 3,
  ONE_PAIR: 1,
  NO_WIN: 0,
};

const PAYOUT_DENOMINATORS = {
  FULL_HOUSE: 2,
  STRAIGHT: 2,
  TWO_PAIR: 2,
};

const cardAssetContext = require.context(
  "../../../assets/cards/DeckNoJokers",
  false,
  /\.png$/i
);

function normaliseAssetModule(assetModule) {
  return assetModule?.default || assetModule;
}

const CARD_IMAGES = cardAssetContext.keys().reduce((accumulator, key) => {
  const cardId = key.replace(/^\.\//, "").replace(/\.png$/i, "").toLowerCase();
  accumulator[cardId] = normaliseAssetModule(cardAssetContext(key));
  return accumulator;
}, {});

function delay(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatPogs(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(
    Number.isFinite(amount) ? amount : 0
  );
}

function useArcadeWalletAutoScale(displayValue) {
  const frameRef = useRef(null);
  const valueRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const frame = frameRef.current;
    const value = valueRef.current;
    if (!frame || !value) return undefined;

    const measure = () => {
      const availableWidth = Math.max(1, frame.clientWidth);
      const naturalWidth = Math.max(1, value.scrollWidth);
      const nextScale = Math.min(1, availableWidth / naturalWidth);
      setScale((current) =>
        Math.abs(current - nextScale) < 0.002 ? current : nextScale
      );
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(frame);
      observer.observe(value);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure, { passive: true });
    return () => window.removeEventListener("resize", measure);
  }, [displayValue]);

  return { frameRef, valueRef, scale };
}

function isLocalPreviewHost() {
  return LOCAL_PREVIEW_HOSTS.has(window.location.hostname);
}

function shuffleDeck(sourceDeck) {
  const deck = [...sourceDeck];

  for (let index = deck.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [deck[index], deck[swapIndex]] = [deck[swapIndex], deck[index]];
  }

  return deck;
}

function parseCard(cardId) {
  const match = String(cardId || "").toLowerCase().match(/^(10|[2-9jqka])([cdhs])$/);
  if (!match) return null;

  const rank = match[1];
  const suit = match[2];
  const valueMap = { j: 11, q: 12, k: 13, a: 14 };

  return {
    id: `${rank}${suit}`,
    rank,
    suit,
    value: valueMap[rank] || Number(rank),
  };
}

function getCardLabel(cardId) {
  const card = parseCard(cardId);
  if (!card) return "Playing card";

  const rankNames = { j: "Jack", q: "Queen", k: "King", a: "Ace" };
  const suitNames = {
    c: "Clubs",
    d: "Diamonds",
    h: "Hearts",
    s: "Spades",
  };

  return `${rankNames[card.rank] || card.rank} of ${suitNames[card.suit]}`;
}

function evaluateHand(cardIds) {
  const cards = cardIds.map(parseCard);
  if (cards.length !== 5 || cards.some((card) => !card)) {
    return { rank: "NO_WIN", label: "NO WIN" };
  }

  const values = cards.map((card) => card.value).sort((a, b) => a - b);
  const suits = cards.map((card) => card.suit);
  const uniqueValues = [...new Set(values)];
  const isFlush = new Set(suits).size === 1;
  const isAceLow = JSON.stringify(values) === JSON.stringify([2, 3, 4, 5, 14]);
  const isNormalStraight =
    uniqueValues.length === 5 &&
    uniqueValues.every((value, index) => index === 0 || value === uniqueValues[index - 1] + 1);
  const isStraight = isAceLow || isNormalStraight;
  const isRoyal = JSON.stringify(values) === JSON.stringify([10, 11, 12, 13, 14]);

  const counts = values.reduce((accumulator, value) => {
    accumulator[value] = (accumulator[value] || 0) + 1;
    return accumulator;
  }, {});

  const groups = Object.values(counts).sort((a, b) => b - a);

  if (isFlush && isRoyal) return { rank: "ROYAL_FLUSH", label: "ROYAL FLUSH" };
  if (isFlush && isStraight) return { rank: "STRAIGHT_FLUSH", label: "STRAIGHT FLUSH" };
  if (groups[0] === 4) return { rank: "FOUR_OF_A_KIND", label: "FOUR OF A KIND" };
  if (groups[0] === 3 && groups[1] === 2) return { rank: "FULL_HOUSE", label: "FULL HOUSE" };
  if (isFlush) return { rank: "FLUSH", label: "FLUSH" };
  if (isStraight) return { rank: "STRAIGHT", label: "STRAIGHT" };
  if (groups[0] === 3) return { rank: "THREE_OF_A_KIND", label: "THREE OF A KIND" };
  if (groups[0] === 2 && groups[1] === 2) return { rank: "TWO_PAIR", label: "TWO PAIR" };
  if (groups[0] === 2) return { rank: "ONE_PAIR", label: "ONE PAIR" };
  return { rank: "NO_WIN", label: "NO WIN" };
}

function calculatePreviewPayout(rank, bet) {
  const numerator = PAYOUT_NUMERATORS[rank] ?? 0;
  const denominator = PAYOUT_DENOMINATORS[rank] ?? 1;
  return (Number(bet || 0) * numerator) / denominator;
}

function createPreviewRound(bet) {
  const deck = shuffleDeck(FULL_DECK);
  return {
    roundId: `preview_${Date.now().toString(36).toUpperCase()}`,
    game: "video_poker",
    status: "DEALT",
    active: true,
    bet,
    hand: deck.slice(0, 5),
    initialHand: deck.slice(0, 5),
    finalHand: null,
    heldIndices: [],
    replacements: [],
    handRank: null,
    handLabel: null,
    payout: null,
    netResult: null,
    outcome: null,
    createdAt: new Date().toISOString(),
    _previewDeck: deck,
  };
}

function settlePreviewRound(round, heldIndices) {
  const held = new Set(heldIndices);
  const discardIndices = [0, 1, 2, 3, 4].filter((index) => !held.has(index));
  const replacements = round._previewDeck.slice(5, 5 + discardIndices.length);
  const finalHand = [...round.initialHand];

  discardIndices.forEach((cardIndex, replacementIndex) => {
    finalHand[cardIndex] = replacements[replacementIndex];
  });

  const result = evaluateHand(finalHand);
  const payout = calculatePreviewPayout(result.rank, round.bet);
  const netResult = payout - round.bet;

  return {
    ...round,
    status: "SETTLED",
    active: false,
    hand: finalHand,
    finalHand,
    heldIndices: [...heldIndices].sort((a, b) => a - b),
    replacements,
    handRank: result.rank,
    handLabel: result.label,
    payout,
    netResult,
    outcome: netResult > 0 ? "WIN" : netResult === 0 ? "BREAK_EVEN" : "LOSS",
    settledAt: new Date().toISOString(),
  };
}

function useVideoPokerSfx(muted) {
  const contextRef = useRef(null);

  const getContext = useCallback(() => {
    if (typeof window === "undefined") return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;

    if (!contextRef.current) {
      contextRef.current = new AudioContextClass();
    }

    if (contextRef.current.state === "suspended") {
      contextRef.current.resume().catch(() => {});
    }

    return contextRef.current;
  }, []);

  const tone = useCallback((frequency, duration = 0.07, gain = 0.035, type = "sine", delaySeconds = 0) => {
    if (muted) return;
    const context = getContext();
    if (!context) return;

    const oscillator = context.createOscillator();
    const amp = context.createGain();
    const startsAt = context.currentTime + delaySeconds;
    const endsAt = startsAt + duration;

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startsAt);
    amp.gain.setValueAtTime(0.0001, startsAt);
    amp.gain.exponentialRampToValueAtTime(gain, startsAt + 0.008);
    amp.gain.exponentialRampToValueAtTime(0.0001, endsAt);

    oscillator.connect(amp);
    amp.connect(context.destination);
    oscillator.start(startsAt);
    oscillator.stop(endsAt + 0.01);
  }, [getContext, muted]);

  const playFlip = useCallback(() => {
    tone(520, 0.045, 0.025, "triangle");
    tone(760, 0.04, 0.018, "triangle", 0.035);
  }, [tone]);

  const playHold = useCallback((active) => {
    tone(active ? 660 : 360, 0.065, 0.03, "square");
  }, [tone]);

  const playDraw = useCallback(() => {
    tone(180, 0.09, 0.04, "sawtooth");
    tone(280, 0.09, 0.026, "triangle", 0.08);
  }, [tone]);

  const playResult = useCallback((outcome) => {
    if (outcome === "WIN") {
      [523, 659, 784, 1047].forEach((frequency, index) => {
        tone(frequency, 0.14, 0.035, "triangle", index * 0.09);
      });
      return;
    }

    if (outcome === "BREAK_EVEN") {
      tone(440, 0.1, 0.025, "triangle");
      tone(520, 0.1, 0.02, "triangle", 0.08);
      return;
    }

    tone(220, 0.12, 0.03, "sine");
    tone(165, 0.16, 0.025, "sine", 0.1);
  }, [tone]);

  useEffect(() => {
    return () => {
      if (contextRef.current && contextRef.current.state !== "closed") {
        contextRef.current.close().catch(() => {});
      }
    };
  }, []);

  return { playFlip, playHold, playDraw, playResult };
}

function VideoPoker() {
  const [phase, setPhase] = useState("LANDING");
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [error, setError] = useState("");
  const [round, setRound] = useState(null);
  const [displayedHand, setDisplayedHand] = useState([]);
  const [heldIndices, setHeldIndices] = useState([]);
  const [revealed, setRevealed] = useState([false, false, false, false, false]);
  const [discardingIndices, setDiscardingIndices] = useState([]);
  const [incomingIndices, setIncomingIndices] = useState([]);
  const [betInput, setBetInput] = useState(String(DEFAULT_BET));
  const [dealing, setDealing] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [resultVisible, setResultVisible] = useState(false);
  const [muted, setMuted] = useState(false);
  const animationTokenRef = useRef(0);

  const formattedBalance = loading ? "—" : formatPogs(balance);
  const {
    frameRef: arcadeWalletFrameRef,
    valueRef: arcadeWalletValueRef,
    scale: arcadeWalletScale,
  } = useArcadeWalletAutoScale(formattedBalance);
  const { playFlip, playHold, playDraw, playResult } = useVideoPokerSfx(muted);

  const revealCardsSequentially = useCallback(async (indices) => {
    const token = animationTokenRef.current;

    for (const index of indices) {
      if (animationTokenRef.current !== token) return;
      await delay(CARD_FLIP_GAP_MS);
      if (animationTokenRef.current !== token) return;
      setRevealed((current) => current.map((value, cardIndex) => cardIndex === index ? true : value));
      playFlip();
    }

    await delay(CARD_FLIP_DURATION_MS * 0.45);
  }, [playFlip]);

  const loadRoundIntoView = useCallback(async (nextRound, animate = true) => {
    const hand = Array.isArray(nextRound?.hand)
      ? nextRound.hand
      : Array.isArray(nextRound?.initialHand)
        ? nextRound.initialHand
        : [];

    setRound(nextRound || null);
    setResultVisible(nextRound?.status === "SETTLED");
    setDisplayedHand(hand);
    setHeldIndices(Array.isArray(nextRound?.heldIndices) ? nextRound.heldIndices : []);
    setDiscardingIndices([]);
    setIncomingIndices([]);

    if (!nextRound || hand.length !== 5) {
      setRevealed([false, false, false, false, false]);
      return;
    }

    if (!animate || nextRound.status === "SETTLED") {
      setRevealed([true, true, true, true, true]);
      return;
    }

    setRevealed([false, false, false, false, false]);
    await revealCardsSequentially([0, 1, 2, 3, 4]);
  }, [revealCardsSequentially]);

  const restoreState = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [profile, active] = await Promise.all([
        getVideoPokerProfile(),
        getActiveVideoPokerRound(),
      ]);

      setPreviewMode(false);
      setBalance(Number(profile?.wallet?.pogs || 0));

      if (active?.active && active?.round) {
        await loadRoundIntoView(active.round, false);
      } else {
        setRound(null);
        setDisplayedHand([]);
      }
    } catch (restoreError) {
      if (isLocalPreviewHost()) {
        setPreviewMode(true);
        setBalance(DEV_PREVIEW_BALANCE);
        setRound(null);
        setDisplayedHand([]);
        setError("");
      } else {
        console.error("Video Poker state restore failed:", restoreError);
        setPreviewMode(false);
        setError(
          restoreError instanceof VideoPokerApiError
            ? restoreError.message
            : "VIDEO POKER STATE UNAVAILABLE"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [loadRoundIntoView]);

  useEffect(() => {
    restoreState();
    return () => {
      animationTokenRef.current += 1;
    };
  }, [restoreState]);

  const parsedBet = Number(betInput);
  const validBet =
    Number.isSafeInteger(parsedBet) &&
    parsedBet >= MIN_BET &&
    parsedBet <= MAX_SAFE_BET &&
    parsedBet % 2 === 0;

  const canDeal =
    !loading &&
    !dealing &&
    !drawing &&
    !round?.active &&
    validBet &&
    parsedBet <= balance;

  const handSettled = round?.status === "SETTLED";
  const canHold = Boolean(round?.active && round?.status === "DEALT" && !dealing && !drawing);
  const canDraw = canHold && displayedHand.length === 5 && revealed.every(Boolean);

  const resultText = useMemo(() => {
    if (!handSettled) return "";
    if (round?.outcome === "WIN") {
      return `${round.handLabel || "WIN"} — WON ${formatPogs(round.payout)} POGS`;
    }
    if (round?.outcome === "BREAK_EVEN") {
      return `${round.handLabel || "ONE PAIR"} — BET RETURNED`;
    }
    return "NO WINNING HAND";
  }, [handSettled, round]);

  const handleToggleHold = (index) => {
    if (!canHold) return;

    setHeldIndices((current) => {
      const active = current.includes(index);
      const next = active
        ? current.filter((value) => value !== index)
        : [...current, index].sort((a, b) => a - b);
      playHold(!active);
      return next;
    });
  };

  const handleDeal = async () => {
    setError("");

    if (!validBet) {
      setError("BET MUST BE AN EVEN WHOLE POG AMOUNT OF 2 OR MORE");
      return;
    }

    if (parsedBet > balance) {
      setError("INSUFFICIENT POGS");
      return;
    }

    setDealing(true);
    animationTokenRef.current += 1;

    try {
      let response;

      if (previewMode) {
        const previewRound = createPreviewRound(parsedBet);
        response = {
          round: previewRound,
          balance: balance - parsedBet,
        };
      } else {
        response = await dealVideoPokerRound(parsedBet);
      }

      setBalance(Number(response.balance || 0));
      setHeldIndices([]);
      setResultVisible(false);
      await loadRoundIntoView(response.round, true);
    } catch (dealError) {
      console.error("Video Poker deal failed:", dealError);

      if (dealError instanceof VideoPokerApiError && dealError.code === "ACTIVE_ROUND_EXISTS") {
        await restoreState();
        setError("ACTIVE HAND RESTORED");
      } else if (dealError instanceof VideoPokerApiError && dealError.code === "INSUFFICIENT_FUNDS") {
        setError("INSUFFICIENT POGS");
      } else {
        setError(
          dealError instanceof VideoPokerApiError
            ? String(dealError.message || "DEAL FAILED").toUpperCase()
            : "DEAL FAILED"
        );
      }
    } finally {
      setDealing(false);
    }
  };

  const handleDraw = async () => {
    if (!canDraw || !round) return;

    setDrawing(true);
    setResultVisible(false);
    setError("");
    playDraw();

    const discardIndices = [0, 1, 2, 3, 4].filter(
      (index) => !heldIndices.includes(index)
    );
    setDiscardingIndices(discardIndices);

    try {
      const requestPromise = previewMode
        ? Promise.resolve({
            round: settlePreviewRound(round, heldIndices),
          })
        : drawVideoPokerRound(round.roundId, heldIndices);

      const [response] = await Promise.all([
        requestPromise,
        delay(discardIndices.length > 0 ? DISCARD_DURATION_MS : 120),
      ]);

      const settledRound = response.round;
      const nextHand = settledRound?.finalHand || settledRound?.hand || [];
      const nextBalance = previewMode
        ? balance + Number(settledRound?.payout || 0)
        : Number(response.balance || 0);

      setDiscardingIndices([]);
      setDisplayedHand(nextHand);
      setRound(settledRound);
      setBalance(nextBalance);

      if (discardIndices.length > 0) {
        setRevealed((current) =>
          current.map((value, index) =>
            discardIndices.includes(index) ? false : value
          )
        );
        setIncomingIndices(discardIndices);
        await delay(REPLACEMENT_SETTLE_MS);
        setIncomingIndices([]);
        await revealCardsSequentially(discardIndices);
      }

      playResult(settledRound?.outcome || "LOSS");
      setResultVisible(true);
    } catch (drawError) {
      console.error("Video Poker draw failed:", drawError);
      setDiscardingIndices([]);
      setIncomingIndices([]);
      setError(
        drawError instanceof VideoPokerApiError
          ? String(drawError.message || "DRAW FAILED").toUpperCase()
          : "DRAW FAILED"
      );

      if (!previewMode) {
        await restoreState();
      }
    } finally {
      setDrawing(false);
    }
  };

  const handleNewHand = () => {
    animationTokenRef.current += 1;
    setRound(null);
    setDisplayedHand([]);
    setHeldIndices([]);
    setRevealed([false, false, false, false, false]);
    setResultVisible(false);
    setDiscardingIndices([]);
    setIncomingIndices([]);
    setError("");
  };

  const renderArcadeHeader = () => (
    <div className="video-poker-topbar pogforts-arcade-topbar">
      <div>
        <p className="video-poker-kicker">POGFORTS ARCADE</p>
        <h1 className="video-poker-title">VIDEO POKER</h1>
        <p className="video-poker-subtitle">HOLD. DRAW. WIN.</p>
      </div>

      <div className="video-poker-wallet pogforts-arcade-wallet">
        <span>{previewMode ? "PREVIEW POG BALANCE" : "POG BALANCE"}</span>
        <strong ref={arcadeWalletFrameRef}>
          <span
            ref={arcadeWalletValueRef}
            className="pogforts-arcade-wallet-value"
            style={{ "--pog-wallet-scale": arcadeWalletScale }}
          >
            {!loading && Number(balance) !== 0 && (
              <img src={pogImage} alt="" aria-hidden="true" draggable="false" />
            )}
            {formattedBalance}
          </span>
        </strong>
      </div>
    </div>
  );

  if (phase === "LANDING") {
    return (
      <div className="video-poker-shell">
        <Helmet>
          <title>Video Poker - Pogforts</title>
          <meta
            name="description"
            content="Play Pogforts Video Poker. Deal five cards, hold the cards you want, draw once and settle the final poker hand using Pogs."
          />
        </Helmet>

        <section className="video-poker-panel">
          {renderArcadeHeader()}

          <div className="video-poker-landing-stage">
            <div className="video-poker-landing-pattern" aria-hidden="true">
              <img src={cardBackImage} alt="" draggable="false" />
              <img src={cardBackImage} alt="" draggable="false" />
              <img src={cardBackImage} alt="" draggable="false" />
            </div>
            <div className="video-poker-landing-shade" />

            <div className="video-poker-landing-content">
              <div className="video-poker-landing-card-mark" aria-hidden="true">
                <img src={cardBackImage} alt="" draggable="false" />
              </div>

              <h2>DEAL. HOLD. DRAW.</h2>
              <p className="video-poker-landing-copy">
                Take five cards, lock the ones worth keeping, draw once from the remaining deck and settle your final hand.
              </p>

              <div className="video-poker-landing-flow">
                <div>
                  <span>01</span>
                  <strong>DEAL</strong>
                  <small>FIVE UNIQUE CARDS</small>
                </div>
                <div>
                  <span>02</span>
                  <strong>HOLD</strong>
                  <small>KEEP 0–5 CARDS</small>
                </div>
                <div>
                  <span>03</span>
                  <strong>DRAW</strong>
                  <small>REPLACE THE REST</small>
                </div>
              </div>

              <div className="video-poker-mini-paytable" aria-label="Video Poker payout table">
                {PAYTABLE.map((row) => (
                  <div key={row.key}>
                    <span>{row.label}</span>
                    <strong>{row.multiplier}</strong>
                  </div>
                ))}
              </div>

              {previewMode && (
                <p className="video-poker-preview-note">
                  DEVELOPMENT PREVIEW — MOCK {formatPogs(DEV_PREVIEW_BALANCE)} POG WALLET — NO DATABASE
                </p>
              )}

              {error && (
                <p className="video-poker-error" role="alert">
                  {error}
                </p>
              )}

              <button
                type="button"
                className="video-poker-primary-button video-poker-enter-button"
                onClick={() => setPhase("GAME")}
                disabled={loading && !previewMode}
              >
                ENTER VIDEO POKER
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="video-poker-shell">
      <Helmet>
        <title>Video Poker - Pogforts</title>
        <meta
          name="description"
          content="Play five-card draw Video Poker in the Pogforts Arcade using Pogs."
        />
      </Helmet>

      <section className="video-poker-panel video-poker-live-panel">
        {renderArcadeHeader()}

        <div className="video-poker-game-stage">
          <button
            type="button"
            className="pogforts-arcade-sound-toggle"
            aria-label={muted ? "Enable Video Poker sounds" : "Disable Video Poker sounds"}
            title={muted ? "Enable sounds" : "Disable sounds"}
            onClick={() => setMuted((current) => !current)}
          >
            <span aria-hidden="true">{muted ? "🔇" : "🔊"}</span>
          </button>

          {previewMode && (
            <p className="video-poker-preview-note video-poker-live-preview-note">
              DEVELOPMENT PREVIEW — MOCK {formatPogs(DEV_PREVIEW_BALANCE)} POG WALLET — NO DATABASE
            </p>
          )}

          <div className="video-poker-status-row">
            <div>
              <span>BET</span>
              <strong>{round ? `${formatPogs(round.bet)} POGS` : "READY"}</strong>
            </div>
            <div>
              <span>HAND</span>
              <strong>{handSettled ? round?.handLabel || "NO WIN" : round?.active ? "HOLD OR DRAW" : "WAITING"}</strong>
            </div>
            <div>
              <span>DECK</span>
              <strong>{round?.active ? "47 REMAINING" : handSettled ? "ROUND SETTLED" : "52 CARDS"}</strong>
            </div>
          </div>

          {!round && (
            <div className="video-poker-bet-panel">
              <label htmlFor="video-poker-bet">POG BET</label>
              <div className="video-poker-bet-control">
                <img src={pogImage} alt="" aria-hidden="true" draggable="false" />
                <input
                  id="video-poker-bet"
                  type="number"
                  inputMode="numeric"
                  min={MIN_BET}
                  step="2"
                  max={Math.min(balance || MAX_SAFE_BET, MAX_SAFE_BET)}
                  value={betInput}
                  onChange={(event) => setBetInput(event.target.value)}
                  disabled={dealing || drawing || loading}
                />
              </div>
              <small>EVEN WHOLE POG BETS ONLY — REQUIRED FOR EXACT HALF-PAYOUT ODDS</small>
              <button
                type="button"
                className="video-poker-primary-button"
                onClick={handleDeal}
                disabled={!canDeal}
              >
                {dealing ? "DEALING..." : "DEAL"}
              </button>
            </div>
          )}

          {round && (
            <div className="video-poker-table" aria-live="polite">
              <div className="video-poker-hand" aria-label="Five card poker hand">
                {[0, 1, 2, 3, 4].map((index) => {
                  const cardId = displayedHand[index];
                  const isHeld = heldIndices.includes(index);
                  const isDiscarding = discardingIndices.includes(index);
                  const isIncoming = incomingIndices.includes(index);
                  const isRevealed = revealed[index];

                  return (
                    <div className="video-poker-card-column" key={`slot-${index}`}>
                      <div
                        className={[
                          "video-poker-card",
                          isRevealed ? "is-revealed" : "",
                          isHeld ? "is-held" : "",
                          isDiscarding ? "is-discarding" : "",
                          isIncoming ? "is-incoming" : "",
                        ].filter(Boolean).join(" ")}
                        aria-label={isRevealed && cardId ? getCardLabel(cardId) : "Face-down playing card"}
                      >
                        <div className="video-poker-card-inner">
                          <div className="video-poker-card-face video-poker-card-back">
                            <img src={cardBackImage} alt="Card back" draggable="false" />
                          </div>
                          <div className="video-poker-card-face video-poker-card-front">
                            {cardId && CARD_IMAGES[cardId] ? (
                              <img src={CARD_IMAGES[cardId]} alt={getCardLabel(cardId)} draggable="false" />
                            ) : (
                              <span>?</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className={`video-poker-hold-button ${isHeld ? "is-held" : ""}`}
                        onClick={() => handleToggleHold(index)}
                        disabled={!canHold}
                        aria-pressed={isHeld}
                      >
                        {isHeld ? "HELD" : "HOLD"}
                      </button>
                    </div>
                  );
                })}
              </div>

              {!handSettled && (
                <button
                  type="button"
                  className="video-poker-primary-button video-poker-draw-button"
                  onClick={handleDraw}
                  disabled={!canDraw}
                >
                  {drawing ? "DRAWING..." : "DRAW"}
                </button>
              )}

              {handSettled && resultVisible && (
                <div className={`video-poker-result video-poker-result-${String(round?.outcome || "LOSS").toLowerCase()}`}>
                  <span>{round?.handLabel || "NO WIN"}</span>
                  <strong>{resultText}</strong>
                  <div>
                    <small>PAYOUT</small>
                    <b>
                      {Number(round?.payout || 0) !== 0 && (
                        <img src={pogImage} alt="" aria-hidden="true" draggable="false" />
                      )}
                      {formatPogs(round?.payout || 0)}
                    </b>
                  </div>
                  <button
                    type="button"
                    className="video-poker-primary-button"
                    onClick={handleNewHand}
                  >
                    NEW HAND
                  </button>
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="video-poker-error video-poker-live-error" role="alert">
              {error}
            </p>
          )}

          <div className="video-poker-paytable">
            {PAYTABLE.map((row) => (
              <div key={row.key} className={round?.handRank === row.key ? "is-active" : ""}>
                <span>{row.label}</span>
                <strong>{row.multiplier}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default VideoPoker;
